import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/marketing/SEO";

const Neuchatel = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Piktable Neuchâtel",
    "description": "Solution de réservation pour restaurants à Neuchâtel",
    "url": "https://piktable.ch/neuchatel",
    "areaServed": {
      "@type": "City",
      "name": "Neuchâtel",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Canton de Neuchâtel"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "46.9890",
      "longitude": "6.9298"
    },
    "priceRange": "CHF 10-99",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Neuchâtel",
      "addressCountry": "CH"
    },
    "email": "contact@piktable.ch",
    "sameAs": ["https://www.instagram.com/piktable"],
    "image": "https://piktable.ch/screenshots/dashboard-overview.png"
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://piktable.ch/" },
      { "@type": "ListItem", "position": 2, "name": "Neuchâtel", "item": "https://piktable.ch/neuchatel" }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Solution de réservation pour restaurants à Neuchâtel"
        description="Piktable accompagne les restaurants neuchâtelois dans la digitalisation de leurs réservations. Solution locale : 49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs. Zéro commission par couvert."
        canonical="/neuchatel"
        structuredData={[structuredData, breadcrumbData]}
      />
      <Header />
      <main className="pt-16">
        <section className="py-24 bg-gradient-hero">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="font-playfair text-5xl md:text-6xl font-bold text-foreground">
                Solution de réservation pour restaurants à Neuchâtel
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Piktable accompagne les restaurants neuchâtelois dans la digitalisation de leurs réservations. Solution suisse romande, pensée pour les petits établissements.
              </p>
              <div className="flex justify-center pt-6">
                <Button size="lg" onClick={() => navigate("/signup")}>
                  Essayer gratuitement
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="text-center">
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Piktable pour votre restaurant neuchâtelois
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🏔️</div>
                  <h3 className="text-2xl font-bold text-foreground">Solution locale</h3>
                  <p className="text-muted-foreground">
                    Développé en Suisse romande pour les restaurants de la région. Support en français, facturation en CHF.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">⚡</div>
                  <h3 className="text-2xl font-bold text-foreground">Mise en place rapide</h3>
                  <p className="text-muted-foreground">
                    Pas de formation nécessaire, interface intuitive. Vous êtes opérationnel en quelques minutes seulement.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">📊</div>
                  <h3 className="text-2xl font-bold text-foreground">Vue d'ensemble claire</h3>
                  <p className="text-muted-foreground">
                    Tableau de bord temps réel, historique des réservations, notes clients. Tout ce dont vous avez besoin.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">💡</div>
                  <h3 className="text-2xl font-bold text-foreground">Prix accessible</h3>
                  <p className="text-muted-foreground">
                    49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs du Printemps 2026. Zéro commission par couvert, idéal pour les petits établissements.
                  </p>
                </div>
              </div>

              <div className="mt-12 space-y-4">
                <h3 className="font-playfair text-2xl font-bold text-foreground">
                  Neuchâtel : entre lac et arc jurassien
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Neuchâtel est une ville au bord du lac qui vit au rythme des saisons. L'été, les terrasses du centre-ville et du bord du lac affichent complet, tandis que l'hiver, les restaurants accueillent une clientèle plus locale et régulière. La région neuchâteloise s'étend aussi vers Hauterive et l'arc jurassien — jusqu'au Locle et La Chaux-de-Fonds — où les restaurateurs ont besoin d'outils simples pour gérer leurs réservations sans complexité inutile. Piktable permet de gérer les variations saisonnières de fréquentation, d'ajuster vos créneaux horaires facilement et d'offrir à vos clients une expérience de réservation moderne et fluide.
                </p>
              </div>

              <div className="mt-16 p-8 rounded-2xl bg-primary/5 border-2 border-primary/20">
                <h3 className="font-playfair text-3xl font-bold text-foreground mb-4 text-center">
                  Pour tous les restaurants de Neuchâtel
                </h3>
                <p className="text-lg text-muted-foreground text-center mb-8">
                  Du centre-ville aux bords du lac, Piktable s'adapte à votre établissement et à vos besoins spécifiques.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                    Tester gratuitement
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full sm:w-auto"
                  >
                    En savoir plus
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Neuchatel;
